import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useGuardarMapeoReceta,
  useProductosEnsambladosBC,
  useRecetasBlendMapeo,
} from '@/hooks/useMapeoRecetas';
import { nombrePlantaDisplay } from '@/lib/plantas';
import { cn } from '@/lib/utils';
import type { RecetaBlendMapeo } from '@adelante/shared';
import { useMemo } from 'react';

/**
 * `/admin/mapeo-recetas` — asocia cada receta Blend (texto libre que
 * escribe cada planta en el dosificador) a un producto ensamblado real de
 * Business Central.
 *
 * Por qué existe: el match automático usa el texto exacto del nombre de
 * receta. Si una planta lo escribe distinto a otra para la misma mezcla
 * (ej. "Diseno pavimento" en Roja vs "Diseño Pavimento" en Bianca), la
 * colada queda "sin mapeo a BC" y no se puede enviar el pedido. Acá se
 * corrige asignando manualmente el producto BC correcto — sin depender de
 * que las plantas escriban igual.
 */
export function AdminMapeoRecetasPantalla() {
  const consultaRecetas = useRecetasBlendMapeo();
  const consultaProductos = useProductosEnsambladosBC();
  const guardar = useGuardarMapeoReceta();

  const recetas = consultaRecetas.data?.data?.recetas ?? [];
  const errorRecetas =
    consultaRecetas.data?.error?.mensaje ?? (consultaRecetas.error?.message || null);

  const productos = consultaProductos.data?.data?.productos ?? [];
  const errorProductos =
    consultaProductos.data?.error?.mensaje ?? (consultaProductos.error?.message || null);

  // Agrupar por planta para que sea fácil comparar Roja vs Bianca.
  const grupos = useMemo(() => {
    const m = new Map<string, RecetaBlendMapeo[]>();
    for (const r of recetas) {
      const arr = m.get(r.planta_codigo) ?? [];
      arr.push(r);
      m.set(r.planta_codigo, arr);
    }
    return Array.from(m.entries());
  }, [recetas]);

  const guardarMapeo = (receta: RecetaBlendMapeo, codigo_bc: string) => {
    const producto = productos.find((p) => p.codigo === codigo_bc);
    if (!producto) return;
    guardar.mutate({
      id_receta_blend: receta.id_receta_blend,
      codigo_bc: producto.codigo,
      descripcion_bc: producto.descripcion,
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Mapeo Receta Blend → BC</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Asocia el nombre de receta tal como lo escribe cada planta en el dosificador con el
          producto ensamblado correspondiente en Business Central. Necesario para que las coladas
          puedan enviarse a BC — si una receta queda "sin mapear", el envío se bloquea.
        </p>
      </div>

      {errorRecetas && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="p-4 text-sm text-destructive">{errorRecetas}</CardContent>
        </Card>
      )}
      {errorProductos && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="p-4 text-sm text-destructive">
            No se pudo cargar el catálogo de productos de Business Central: {errorProductos}
          </CardContent>
        </Card>
      )}

      {consultaRecetas.isLoading && (
        <Card>
          <CardContent className="space-y-2 p-4">
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-6 w-1/3" />
            <Skeleton className="h-6 w-2/3" />
          </CardContent>
        </Card>
      )}

      {!consultaRecetas.isLoading &&
        grupos.map(([plantaCodigo, items]) => (
          <Card key={plantaCodigo}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">
                {nombrePlantaDisplay(plantaCodigo)}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {items.map((r) => (
                <div
                  key={r.id_receta_blend}
                  className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{r.nombre_texto}</div>
                    {r.codigo_bc ? (
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        BC: {r.codigo_bc} — {r.descripcion_bc}
                      </div>
                    ) : (
                      <Badge variant="destructive" className="mt-1">
                        Sin mapeo a BC
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <Select
                      value={r.codigo_bc ?? ''}
                      onValueChange={(v) => guardarMapeo(r, v)}
                      disabled={guardar.isPending || productos.length === 0}
                    >
                      <SelectTrigger
                        className={cn(
                          'h-8 w-[280px] text-xs',
                          !r.codigo_bc && 'border-destructive',
                        )}
                      >
                        <SelectValue placeholder="Elegir producto BC..." />
                      </SelectTrigger>
                      <SelectContent>
                        {productos.map((p) => (
                          <SelectItem key={p.codigo} value={p.codigo}>
                            {p.codigo} — {p.descripcion}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
    </div>
  );
}
